import * as Tabs from '@radix-ui/react-tabs';
import { ReactNode, useContext, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { TabNavigationContext } from './_tab_navigation.context';

export type TabNavigationContentProps = {
	/**
	 * A slot to place notification for in
	 */
	trailingSlot?: ReactNode;

	/**
	 * Tabs hide content, so the tab labels need to make it very clear what they
	 * link to, otherwise users will not know if they need to click on them.
	 *
	 * If the team struggles to come up with clear labels, it might be because the way
	 * you’ve separated the content is not clear.
	 */
	label: string;

	/**
	 * Used to provide a special text for assistive technologies. It's required if `notification` is used.
	 * @example <TabNavigation.Content label='Visual label' hiddenText='This is a hidden label' />
	 * @example <TabNavigation.Content label='Messages' notifications={10} hiddenText='Messages. There is 10 new messages' />
	 */
	hiddenLabel?: string;

	/**
	 * Content to be rendered once activated.
	 */
	children?: ReactNode;
	disabled?: boolean;
} & Tabs.TabsContentProps;

const elements = {
	content: styled(Tabs.Content)`
		outline: none;
		width: 100%;
	`,
};

/* @internal */
export function TabNavigationContent({
	children,
	value,
	label,
	hiddenLabel,
	trailingSlot,
	disabled,
	...rest
}: TabNavigationContentProps): JSX.Element {
	const { registerTab, unregisterTab } = useContext(TabNavigationContext);

	useLayoutEffect(
		function whenComponentMounts() {
			registerTab({
				value,
				label,
				hiddenLabel: hiddenLabel ?? null,
				trailingSlot: trailingSlot ?? null,
				removed: false,
				disabled: disabled ?? false,
			});

			return () => {
				unregisterTab(value);
			};
		},
		[value, label, hiddenLabel, trailingSlot, registerTab, unregisterTab, disabled],
	);

	return (
		<elements.content value={value} {...rest}>
			{children}
		</elements.content>
	);
}
